import { useState, useEffect } from 'react';
import GeckoRequest from 'services/gecko-request';
import { useRedux } from 'services/gecko-store/redux';
import getAssignedTo from '../helpers/getAssignedTo.js';
import usePageMassAction from 'ui/components/table/hooks/usePageMassAction';

const mapStateToProps = (state) => {
  return {
    agents: state.agents,
    teams: state.teams,
    channels: state.channels,
  };
};

function useResultsData(searchTerm, startingFilterString) {
  const { agents, teams, channels } = useRedux(mapStateToProps);

  const [results, setResults] = useState([]);
  const [count, setCount] = useState(0);
  const [status, setStatus] = useState('idle'); // Possible values: 'idle', 'loading', 'success', 'error'

  const PAGE_SIZE = 25;
  const [term, setTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [orderBy, setOrderBy] = useState('lastMessageTime');
  const [sortOrder, setSortOrder] = useState('DESC');
  const [filterString, setFilterString] = useState('');

  const [actionTimestamp, setActionTimestamp] = useState('');

  const [selectedChannels, setSelectedChannels] = useState([]);
  const [selectedTags, setSelectedTags] = useState([]);
  const [selectedStatuses, setSelectedStatuses] = useState([]);
  const [selectedAgentsAndTeams, setSelectedAgentsAndTeams] = useState([]);

  const [massAction] = usePageMassAction(results, 'id');

  const applyFilters = (value) => {
    setCurrentPage(1);
    setFilterString(value);
  };

  const buildRow = (conversation) => {
    return {
      contact: conversation.User?.full_name,
      messages: conversation.Messages,
      lastMessageTime: conversation.last_message_time,
      assignedTo: getAssignedTo(agents, teams, conversation),
      lastChannel: getChannelName(conversation.last_message_channel),
      status: conversation.status,
      id: conversation.id,
      tagIds: conversation.tag_ids,
      assignedAgentIds: conversation.assigned_agent_ids,
      assignedTeamIds: conversation.assigned_team_ids,
    };
  };

  const getChannelName = (channelId) => {
    const chan = channels.find((c) => c.id === channelId);
    return chan ? chan.name : null;
  };

  useEffect(() => {
    setStatus('loading');
    if (term) {
      const root = '/search/conversations-new';
      const termQuery = `?term=${term}`;
      const pageQuery = `&page=${currentPage || 1}`;
      const pageSizeQuery = `&pageSize=${PAGE_SIZE || 20}`;
      const sortQuery = `&sort=${sortOrder || 'DESC'}`;
      const orderByQuery = `&orderItem=${orderBy || 'lastMessageTime'}`;
      const filterQueries = `${filterString ? `&${filterString}` : ''}`;

      GeckoRequest.get(
        `${root}${termQuery}${pageQuery}${pageSizeQuery}${sortQuery}${orderByQuery}${filterQueries}`
      )
        .then((res) => {
          setCount(res.count);
          setResults(res.rows.map((r) => buildRow(r)));
          setStatus('success');
        })
        .catch(() => {
          setStatus('error');
        });
    }
  }, [currentPage, orderBy, sortOrder, term, actionTimestamp, filterString]);

  useEffect(() => {
    setCurrentPage(1);
    setSelectedAgentsAndTeams([]);
    setSelectedChannels([]);
    setSelectedTags([]);
    setSelectedStatuses([]);
    setFilterString('');
    setTerm(searchTerm);
    setFilterString(startingFilterString);
  }, [searchTerm]);

  useEffect(() => {
    massAction.setSelected([]);
  }, [term, filterString, actionTimestamp]);

  const pagination = {
    total_items: Number(count),
    total_pages: Math.ceil(Number(count) / PAGE_SIZE),
    current_page: Number(currentPage),
    changePage: (page) => {
      const pageNumber = Number(page);
      if (!isNaN(pageNumber)) {
        setCurrentPage(pageNumber < 1 ? 1 : pageNumber);
      }
    },
    order_by: orderBy,
    sort: sortOrder,
    changeOrder: (sortKey) => {
      if (orderBy === sortKey) {
        setSortOrder(sortOrder === 'ASC' ? 'DESC' : 'ASC');
      } else {
        setOrderBy(sortKey);
      }
    },
  };

  return {
    results,
    count,
    status,
    pagination,
    massAction,
    setSelectedChannels,
    setSelectedAgentsAndTeams,
    setSelectedTags,
    setSelectedStatuses,
    setActionTimestamp,
    selectedChannels,
    selectedAgentsAndTeams,
    selectedTags,
    selectedStatuses,
    applyFilters,
  };
}

export default useResultsData;
